/* Created by Interactive 63 - https://interactive63.com */
/**
 * Pure coordinate maths for manipulating elements on the stage. Everything works on plain
 * matrix and point values (a DOMMatrix satisfies `Matrix`), so it can be tested without a layout
 * engine.
 *
 * The animated node's CSS transform is T(origin) · translate · rotate · scale · skew ·
 * T(-origin). Translate values are in the parent's user space, so screen deltas are converted
 * through the parent's screen matrix. The pivot is fixed under rotation and scale, which makes
 * its screen position a function of the node's current screen matrix.
 */

export interface Point {
  x: number;
  y: number;
}

export interface Matrix {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
}

export interface BBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const EPSILON = 1e-9;

/** Applies a matrix to a point. */
export function applyMatrix(m: Matrix, p: Point): Point {
  return { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
}

/** The inverse of a matrix, or null when it cannot be inverted. */
export function invertMatrix(m: Matrix): Matrix | null {
  const det = m.a * m.d - m.b * m.c;
  if (Math.abs(det) < EPSILON) return null;
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
}

/** Applies only the linear part of a matrix (no translation), for converting vectors. */
export function applyLinear(m: Matrix, v: Point): Point {
  return { x: m.a * v.x + m.c * v.y, y: m.b * v.x + m.d * v.y };
}

/** Converts a screen-space movement into the parent's user space. */
export function screenDeltaToParent(parentScreen: Matrix, dx: number, dy: number): Point {
  const inverse = invertMatrix(parentScreen);
  return inverse ? applyLinear(inverse, { x: dx, y: dy }) : { x: 0, y: 0 };
}

/** The pivot in the node's local space, from its bounding box and origin percentages. */
export function pivotLocal(box: BBox, origin: Point): Point {
  return {
    x: box.x + (origin.x / 100) * box.width,
    y: box.y + (origin.y / 100) * box.height,
  };
}

/** The pivot on screen, given the node's current screen matrix (which includes its transform). */
export function pivotScreen(nodeScreen: Matrix, box: BBox, origin: Point): Point {
  return applyMatrix(nodeScreen, pivotLocal(box, origin));
}

/** Converts a point in the node's local space into origin percentages of its box. */
export function originFromLocal(box: BBox, local: Point): Point {
  return {
    x: box.width === 0 ? 50 : ((local.x - box.x) / box.width) * 100,
    y: box.height === 0 ? 50 : ((local.y - box.y) / box.height) * 100,
  };
}

/** The angle of a vector in degrees, clockwise from the positive x axis on a y-down screen. */
export function angleOf(from: Point, to: Point): number {
  return (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
}

export interface RotationState {
  last: number;
  total: number;
}

/** Starts tracking cumulative rotation from a pointer angle in degrees. */
export function startRotation(angle: number): RotationState {
  return { last: angle, total: 0 };
}

/**
 * Advances cumulative rotation to a new pointer angle. Each step takes the shortest way round,
 * so crossing the ±180° boundary continues counting (360°, 720° and so on) instead of wrapping.
 */
export function advanceRotation(state: RotationState, angle: number): RotationState {
  let step = angle - state.last;
  while (step > 180) step -= 360;
  while (step <= -180) step += 360;
  return { last: angle, total: state.total + step };
}

/** Which scale axes a handle controls. */
export type ScaleAxes = 'both' | 'x' | 'y';

/**
 * The scale multiplier along each of a node's own axes after dragging the pointer from `start`
 * to `current` (screen points) about a screen-space pivot. `startScreen` is the node's screen
 * matrix when the drag began, so the axes follow any rotation, skew or earlier scale. An axis
 * whose starting distance from the pivot is under `minimum` pixels stays at 1.
 */
export function localScaleRatio(
  startScreen: Matrix,
  pivot: Point,
  start: Point,
  current: Point,
  axes: ScaleAxes = 'both',
  minimum = 4,
): Point {
  const inverse = invertMatrix(startScreen);
  if (!inverse) return { x: 1, y: 1 };
  const from = applyLinear(inverse, { x: start.x - pivot.x, y: start.y - pivot.y });
  const to = applyLinear(inverse, { x: current.x - pivot.x, y: current.y - pivot.y });
  const scale = Math.hypot(startScreen.a, startScreen.b) || 1;
  /** The ratio along one axis, guarded against a start point too close to the pivot. */
  const ratio = (before: number, after: number): number =>
    Math.abs(before) * scale < minimum ? 1 : after / before;
  return {
    x: axes === 'y' ? 1 : ratio(from.x, to.x),
    y: axes === 'x' ? 1 : ratio(from.y, to.y),
  };
}

/**
 * Snaps one origin percentage to the box's start, middle or end when it is within `radiusPx`
 * screen pixels. `pxPerPercent` is how many screen pixels one percent spans on this axis.
 */
export function snapOriginAxis(
  percent: number,
  pxPerPercent: number,
  radiusPx: number,
): { value: number; snapped: boolean } {
  for (const mark of [0, 50, 100]) {
    if (Math.abs(percent - mark) * pxPerPercent <= radiusPx) return { value: mark, snapped: true };
  }
  return { value: percent, snapped: false };
}

/** Rounds to a fixed number of decimal places. */
export function roundTo(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export type ResizeCursor = 'ew' | 'ns' | 'nesw' | 'nwse';

/**
 * The resize cursor for a scale handle, from the on-screen angle (degrees, clockwise from the
 * positive x axis) of the line from the box centre to the handle.
 */
export function resizeCursor(angle: number): ResizeCursor {
  const a = ((angle % 180) + 180) % 180;
  if (a < 22.5 || a >= 157.5) return 'ew';
  if (a < 67.5) return 'nwse';
  if (a < 112.5) return 'ns';
  return 'nesw';
}
